import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { ErrorMessage } from '@hookform/error-message';
import classNames from 'classnames';
import { get } from 'lodash';
import { ReactNode, useState } from 'react';
import { Control, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { Form, FormControlProps, InputGroup } from '../../../../bootstrap';
import { Hint } from '../Hint';

// There might be a better way to do this, the errors in TS
// can become confusing if you forget a prop like `label`
type Props<T extends FieldValues> = {
  // Only allow string values to be used with this component
  fieldName: FieldPathByValue<T, string | null | undefined> | FieldPathByValue<T, number | null | undefined>;
  control: Control<T>;
  label?: ReactNode;
  hint?: ReactNode;
  required?: boolean;
  hideErrorMessage?: boolean;
  disabled?: boolean;
  placeholder?: string;
  displayValue?: boolean;
  inputClassnames?: string;
  // Renders the show/hide control as a bare eye icon sitting inside the field, with no label text
  // and no input-group chrome. The accessible name stays on the button's aria-label.
  iconOnlyToggle?: boolean;
} & Pick<
  FormControlProps,
  'autoFocus' | 'style' | 'maxLength' | 'autoComplete' | 'inputMode' | 'autoCapitalize' | 'spellCheck'
>;

export function ControlledPasswordInput<T extends FieldValues>({
  label,
  required,
  hint,
  control,
  fieldName,
  hideErrorMessage,
  displayValue,
  inputClassnames,
  iconOnlyToggle = false,
  ...props
}: Props<T>) {
  const {
    field,
    formState: { errors },
  } = useController({ name: fieldName, control });

  const [showPassword, setShowPassword] = useState(false);

  const feedback = (
    <>
      {hint && <Hint>{hint}</Hint>}
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any} // This *should* work without casting but it doesn't
          render={({ message }) => <div className="u-fs-2 u-color-red1 w-100">{message}</div>}
        />
      )}
    </>
  );

  return (
    <>
      {label && (
        <Form.Label className="u-fs-2 fw-bold">
          {label}
          {required && <span className="u-color-red1 ps-1 ">*</span>}
        </Form.Label>
      )}
      {displayValue ? (
        <div className="u-fs-2">{field.value ?? 'N/A'}</div>
      ) : (
        <>
          <InputGroup className={classNames({ 'input-group--icon-toggle': iconOnlyToggle })}>
            <Form.Control
              className={classNames(inputClassnames, {
                'u-border-color-red1': get(errors, fieldName) && !hideErrorMessage,
                'border-end-0': iconOnlyToggle,
              })}
              type={showPassword ? 'text' : 'password'}
              {...field}
              {...props}
            />
            <InputGroup.Text className={classNames({ 'bg-transparent border-start-0': iconOnlyToggle })}>
              {/* Deliberately not btn-link: these utilities keep the colour and weight the control
                  had as a div, rather than picking up Bootstrap's link blue and underline. */}
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                className="d-flex align-items-center p-0 border-0 bg-transparent text-reset"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                {!iconOnlyToggle && <span className="ms-1">{showPassword ? 'Hide' : 'Show'}</span>}
              </button>
            </InputGroup.Text>
            {!iconOnlyToggle && feedback}
          </InputGroup>
          {/* Outside the group so the group's focus ring wraps only the field, not the messages. */}
          {iconOnlyToggle && feedback}
        </>
      )}
    </>
  );
}
